import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import momentTz from 'moment-timezone';
import { useQuery } from '@apollo/react-hooks';
import { useReactiveVar } from '@apollo/client';
import { GET_PROVIDER_SUPERVISOR } from 'api/graphql/v2/queries/Providers';
import { providerTemplateSummaryVar } from 'utils/cache/calendar/index';
import { IProvider } from 'model/v2';
import ProfilePicture from 'views/components/ui/profile/ProfilePicture';
import { FEATURES, getFeatureAvailability } from 'utils/featureToggle';
import { formatAttendeeName, formatProviderName } from 'utils/format';
import BookedProgress from './BookedProgress';
import CaseloadModal from './CaseloadModal';
import { SidebarWrap } from './style';

interface IProps {
  provider?: IProvider;
}

const ProviderSidebar = ({ provider }: IProps) => {
  const history = useHistory();

  const editStaff = useCallback(() => {
    history.push({
      pathname: `/providers/${provider?.id}/edit`,
      state: {
        title: 'Edit Staff Info',
        provider
      }
    });
  }, [history, provider]);

  const fullName = useMemo(
    () =>
      formatAttendeeName(
        provider?.firstName,
        provider?.lastName,
        provider?.clinic?.abbreviation,
        provider?.speciality?.abbreviation,
        provider?.displayName
      ),
    [provider]
  );

  const subtitle = useMemo(() => {
    const tz = provider?.clinic?.timezone;
    const tzAbbr = tz ? momentTz.tz(tz).format('z') : '';
    const isObservable =
      getFeatureAvailability(FEATURES.SHADOW_SCHEDULING) &&
      provider?.isObservable;
    return [tzAbbr, isObservable ? 'Observable' : '', provider?.employment]
      .filter(Boolean)
      .join(' | ');
  }, [provider]);

  const clinics = provider?.clinic?.name || 'N/A';

  // Clinical supervisor: for CODAs and SLPAs this is a single clinician who is
  // not their UKG organisational supervisor. Looked up from the assistant's
  // side, which is the direction schedulers ask the question in.
  const { data: supervisorData } = useQuery(GET_PROVIDER_SUPERVISOR, {
    variables: { assistantId: Number(provider?.id) },
    skip: !provider?.id
  });

  const supervisor = useMemo(() => {
    const record = supervisorData?.getSupervisor;
    if (!record) return 'N/A';
    return formatProviderName(record) || 'N/A';
  }, [supervisorData]);

  const summary = useReactiveVar(providerTemplateSummaryVar);
  // Only this provider's totals; guards against showing another's caseload.
  const templateSummary =
    summary && Number(summary.providerId) === Number(provider?.id)
      ? summary
      : null;
  const uniqueClients = templateSummary
    ? String(templateSummary.uniqueClients)
    : 'N/A';
  const caseload = templateSummary?.caseload ?? [];
  // Names with dates of birth stay behind a click rather than sitting open on a
  // shared clinic screen.
  const [isCaseloadOpen, setIsCaseloadOpen] = useState(false);

  const isOnline = provider?.isActive !== false;

  return (
    <SidebarWrap>
      <div className="card-header">
        <div className={`avatar-wrap ${isOnline ? 'online' : 'offline'}`}>
          <ProfilePicture email={provider?.email} className="avatar" />
          <span className="status-dot" />
        </div>
        <div className="text">
          <span className="name">{fullName}</span>
          {subtitle && <span className="subtitle">{subtitle}</span>}
        </div>
      </div>

      <BookedProgress provider={provider} />

      <div className="info">
        <div className="info-head">
          <p className="info-title">Provider Information</p>
          <button type="button" className="edit" onClick={editStaff}>
            Edit
          </button>
        </div>

        <div className="info-field">
          <span className="field-label">Email</span>
          <span className="field-value">{provider?.email || 'N/A'}</span>
        </div>

        <div className="info-field">
          <span className="field-label">Clinics</span>
          <span className="field-value">{clinics}</span>
        </div>

        <div className="info-field">
          <span className="field-label">Status</span>
          <span className="field-value">{provider?.status || 'N/A'}</span>
        </div>

        <div className="info-field">
          <span className="field-label">Clinical Supervisor</span>
          <span className="field-value">{supervisor}</span>
        </div>

        <div className="info-field">
          <span className="field-label">
            Unique Clients
            {caseload.length > 0 && (
              <button
                type="button"
                className="breakdown-toggle"
                onClick={() => setIsCaseloadOpen(true)}
              >
                Show clients
              </button>
            )}
          </span>
          <span className="field-value">{uniqueClients}</span>
        </div>
      </div>
      <CaseloadModal
        isOpen={isCaseloadOpen}
        onClose={() => setIsCaseloadOpen(false)}
        providerName={fullName}
        caseload={caseload}
      />
    </SidebarWrap>
  );
};

export default React.memo(ProviderSidebar);
